/**
 * Canonical journey-lane taxonomy — the SINGLE source for these values.
 *
 * The lanes are derived by `deriveLane` in the API (`modules/dashboard`), which
 * imports them from here rather than declaring them, and they reach the browser
 * on `userPallet.listPage`. Both sides need the value set — the API to build
 * the output enum, the web app to key its lane registries — and this leaf
 * package is the lowest layer both can import (the frontend cannot import
 * `apps/api`, and the API must not import `apps/web`). Same reason
 * `pallet-status.ts` lives here.
 */

/**
 * The lanes our own fleet works. Declared separately because BOTH sets below
 * are built from it — the ops set adds the carrier lanes, the customer set adds
 * `owned` — so neither is a hand-maintained copy of the other, and neither is
 * the COMPLEMENT of an unguarded set (`20-architecture.md` calls that shape
 * worse than the copy it replaces: a value added to one array is by
 * construction absent from the thing being excluded, so it lands in the
 * complement and is silently classified as the other thing).
 */
const FLEET_LANES = [
  'ed_outbound',
  'fmd_pending',
  'fsp_pending',
  'fmd_transit',
  'fsp_transit',
  'fsr_outbound',
  'er_close',
  'moved',
  'back_home',
  'stored',
  'rented',
  'closed',
  'cancelled',
] as const

/**
 * The lanes a third-party carrier works — a job that materializes no Stop and
 * takes no driver, so it progresses along `carrier_status` (`carrier-status.ts`)
 * rather than along the pallet's physical status.
 *
 * ONE declaration, so "is this a carrier lane" has one answer. It was four —
 * three of them `startsWith('cm_')` heuristics inside the tests themselves,
 * which meant a differently-named carrier lane would be classified as FLEET by
 * the very assertions written to constrain it, and they would keep passing.
 */
export const CARRIER_DASHBOARD_LANES = [
  'cm_booked',
  'cm_transit',
  'cm_attention',
  'cer_booked',
  'cer_transit',
  'cer_attention',
] as const

export const DASHBOARD_LANES = [
  ...FLEET_LANES,
  ...CARRIER_DASHBOARD_LANES,
] as const

export type DashboardLane = (typeof DASHBOARD_LANES)[number]

export type CarrierDashboardLane = (typeof CARRIER_DASHBOARD_LANES)[number]

const CARRIER_LANE_SET: ReadonlySet<string> = new Set(CARRIER_DASHBOARD_LANES)

/** Is this lane one a third-party carrier works, rather than our own fleet? */
export function isCarrierLane(
  lane: DashboardLane,
): lane is CarrierDashboardLane {
  return CARRIER_LANE_SET.has(lane)
}

/**
 * The lanes a CUSTOMER surface can render.
 *
 * Built from the fleet lanes rather than from `DASHBOARD_LANES`
 * (`[...DASHBOARD_LANES, 'owned']`). That spelling makes the two
 * taxonomies one, so every ops lane is automatically a customer lane and owes
 * catalogue copy — `customer-messages.test.ts` requires a
 * `pallet.lane.<lane>.label` for each. Six carrier entries there would have to
 * be worded identically to their fleet counterparts (`order.step.carrier_move`
 * already rules that a customer is never told who drives the truck), so the
 * guard would force six Ops-editable duplicates that drift the first time
 * somebody rewords one of them at `/ops/messages`.
 *
 * `owned` is the one key wider than the fleet set: a pallet the customer bought
 * outright rests in `rented`, and "Renting" is the one lane word wrong for it.
 * It is not a lane `deriveLane` could return — it is a property of the
 * customer-pallet slot, not of where the box is in its journey.
 */
export const CUSTOMER_LANES = [...FLEET_LANES, 'owned'] as const

export type CustomerLane = (typeof CUSTOMER_LANES)[number]

/** The customer lanes a board lane can fold onto — `owned` is applied after. */
type CustomerDisplayLane = Exclude<CustomerLane, 'owned'>

/**
 * Which customer lane each board lane is SHOWN as.
 *
 * Exhaustive on the key, so a lane added to `DASHBOARD_LANES` fails the build
 * until somebody decides what the customer reads; constrained on the value, so
 * it can only fold onto a lane the catalogue already covers.
 *
 * **Every carrier lane reaches a customer.** No writer links a pallet to a
 * carrier leg bought at checkout, so both customer reads find those legs
 * through the slot's ORDER (`user-pallets/carrier-follow-up.ts`, reading the
 * board's own per-order pick), and the ops create path's linked carrier events
 * through `order_event_pallet` as before. The checkout path is covered by
 * `carrier-pallet-journey.integration.test.ts`, the linked one by
 * `user-pallets.lane.integration.test.ts`.
 *
 * The fold each lane takes is the fleet lane whose existing copy is true of it.
 * ONE reservation, recorded rather than papered over: `cm_attention` covers
 * `exception` as well as `delayed`, and `exception` is defined as "damage, a
 * refused delivery, a lost pallet". `fmd_transit`'s note promises an arrival
 * ("you'll have options once it arrives"), which is true of a late box and not
 * of a lost one. Splitting them needs `delayed` and `exception` to reach
 * different lanes upstream, and the replacement sentence is a copy ruling.
 */
export const CUSTOMER_LANE_OF: Record<DashboardLane, CustomerDisplayLane> = {
  ed_outbound: 'ed_outbound',
  fmd_pending: 'fmd_pending',
  fsp_pending: 'fsp_pending',
  fmd_transit: 'fmd_transit',
  fsp_transit: 'fsp_transit',
  fsr_outbound: 'fsr_outbound',
  er_close: 'er_close',
  moved: 'moved',
  back_home: 'back_home',
  stored: 'stored',
  rented: 'rented',
  closed: 'closed',
  cancelled: 'cancelled',
  cm_booked: 'fmd_pending',
  cm_transit: 'fmd_transit',
  cm_attention: 'fmd_transit',
  cer_booked: 'er_close',
  cer_transit: 'er_close',
  cer_attention: 'er_close',
}

/**
 * The display lane for a pallet row — the expression every customer surface
 * needs, declared once so the household list, the business workspace and the
 * detail page cannot disagree about which pallets read as owned.
 */
export function customerLane(
  lane: DashboardLane,
  owned: boolean,
): CustomerLane {
  const shown = CUSTOMER_LANE_OF[lane]
  return owned && shown === 'rented' ? 'owned' : shown
}

/**
 * The service a lane represents, where the lane says more than the order's own
 * `flow` does — both surfaces that show a journey's type read it as
 * `LANE_FLOW[lane] ?? row.flow`.
 *
 * ONE registry, here rather than per surface. It was two maps, one on the ops
 * board and one on the customer detail page, keyed differently: the board's was
 * `Record<string, …>` and carried no `cancelled`. That produced no defect —
 * both readers spell `LANE_FLOW[lane] ?? row.flow`, and `??` cannot tell a
 * missing key from an explicit `null` — but it is why only one of the two maps
 * would have failed the build when a lane was added.
 */
export const LANE_FLOW: Record<
  DashboardLane,
  'store' | 'move' | 'rent' | null
> = {
  ed_outbound: null,
  fmd_pending: 'move',
  fmd_transit: 'move',
  moved: 'move',
  back_home: 'store',
  fsp_pending: 'store',
  fsp_transit: 'store',
  stored: 'store',
  fsr_outbound: 'store',
  rented: 'rent',
  er_close: null,
  closed: null,
  cancelled: null,
  // A carrier move is a move; a carrier retrieval is a close-out, like
  // `er_close` beside it.
  cm_booked: 'move',
  cm_transit: 'move',
  cm_attention: 'move',
  cer_booked: null,
  cer_transit: null,
  cer_attention: null,
}
